import { useRef, useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

import { ROUTES } from '../constants/routes';
import { useAuth } from '../store/authStore';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const IS_LOCALHOST = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const CAPTCHA_ENABLED = Boolean(SITE_KEY) && !IS_LOCALHOST;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const widgetRef = useRef<TurnstileInstance>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (CAPTCHA_ENABLED && !turnstileToken) {
      setError('Please complete the CAPTCHA verification before continuing.');
      return;
    }

    setLoading(true);
    try {
      await login(email, password, turnstileToken ?? undefined);
      navigate(ROUTES.OVERVIEW, { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to sign in.');
      widgetRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-6 h-6 text-amber-500" />
          <div>
            <h1 className="text-xl font-semibold">Novera Labs Admin</h1>
            <p className="text-sm text-zinc-500">Sign in with an account listed in `ADMIN_EMAILS`.</p>
          </div>
        </div>

        <div className="space-y-4">
          <input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email"
            type="email"
            required
            className="w-full h-11 px-4 rounded-xl bg-zinc-950 border border-zinc-700 text-sm text-zinc-100"
          />
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password"
            required
            className="w-full h-11 px-4 rounded-xl bg-zinc-950 border border-zinc-700 text-sm text-zinc-100"
          />

          {CAPTCHA_ENABLED && (
            <div className="flex justify-center">
              <Turnstile
                ref={widgetRef}
                siteKey={SITE_KEY}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
                options={{ theme: 'dark', size: 'normal' }}
              />
            </div>
          )}

          {error ? <p className="text-sm text-red-400">{error}</p> : null}

          <button
            type="submit"
            disabled={loading || Boolean(CAPTCHA_ENABLED && !turnstileToken)}
            className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : 'Sign In'}
          </button>
        </div>
      </form>
    </div>
  );
}
